import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Phone, MessageCircle, Shield, ChevronDown, Star, AlertTriangle, X,
  Copy, WifiOff, Users, Send,
} from "lucide-react";
import MapView from "@/components/MapView";
import { RideCall, RIDE_CALL_ACTIVE } from "@/components/ride/RideCall";
import { useAuth } from "@/context/AuthContext";
import PlacesMap from "@/components/PlacesMap";
import TranslateChip from "@/components/TranslateChip";
import SpeakChip from "@/components/SpeakChip";
import ReviewSection from "@/components/ReviewSection";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

/**
 * /ride/:delivery_id — Slice I · Grab-style live tracking.
 *
 * · Full-screen leaflet map with driver pin + pickup/drop pins
 * · Polls /delivery/:id (5s) + /delivery/:id/trail (10s) — WebSocket
 *   pushes are a follow-up slice
 * · Bottom-sheet card (driver photo, plate, rating, Chat + Call)
 * · Safety Centre — links /emergency + one-tap panic
 * · Offline banner when the poll errors
 */
export default function RideTrack() {
  const { delivery_id } = useParams();
  const [d, setD]         = useState(null);
  const [trail, setTrail] = useState([]);
  const [offline, setOff] = useState(false);
  const [panicOpen, setPanicOpen] = useState(false);
  const [shareUrl, setShareUrl]   = useState(null);
  const [panicSms, setPanicSms]   = useState(null);
  const [sheetOpen, setSheetOpen] = useState(true);
  const wsRef = useRef(null);
  const { user } = useAuth() || {};
  useSeo({ title: "Live trip — Find2Near" });

  // WebSocket push — subscribes to messenger hub and merges live driver pings.
  // Polling below stays as a redundant fallback for cold reconnects.
  useEffect(() => {
    let closed = false;
    (async () => {
      try {
        const t = await api.get("/messenger/ws-ticket");
        if (closed || !t.data?.ticket) return;
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
        const ws = new WebSocket(`${base}/api/messenger/ws?ticket=${t.data.ticket}`);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "delivery_ping" && msg.delivery_id === delivery_id && msg.ping) {
              setD(prev => prev ? { ...prev, agent_current: msg.ping } : prev);
            }
          } catch { /* ignore malformed */ }
        };
        ws.onclose = () => { wsRef.current = null; };
      } catch { /* WS opt-in — polling still works */ }
    })();
    return () => {
      closed = true;
      if (wsRef.current) wsRef.current.close();
    };
  }, [delivery_id]);

  // Poll delivery detail every 5s
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get(`/delivery/${delivery_id}`);
        if (!cancelled) { setD(r.data); setOff(false); }
      } catch (e) {
        if (!cancelled) setOff(true);
      }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [delivery_id]);

  // Poll trail every 10s
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get(`/delivery/${delivery_id}/trail`);
        if (!cancelled) setTrail(r.data?.trail || r.data || []);
      } catch { /* offline handled by detail poll */ }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [delivery_id]);

  const markers = useMemo(() => {
    const m = [];
    if (d?.pickup?.lat)  m.push({ key: "pu", lat: d.pickup.lat,  lng: d.pickup.lng,  label: "A", popup: <b>Pickup</b> });
    if (d?.dropoff?.lat) m.push({ key: "do", lat: d.dropoff.lat, lng: d.dropoff.lng, label: "B", popup: <b>Drop-off</b> });
    if (d?.agent_current?.lat) m.push({
      key: "drv", lat: d.agent_current.lat, lng: d.agent_current.lng,
      label: "🚕", pulse: true, popup: <b>{d.agent_name || "Driver"}</b>,
    });
    return m;
  }, [d]);

  // Slice O · route line for MapLibre — pickup → driver (if live) → drop-off.
  // A LineString of [lng, lat] coordinates. Null when we can't draw a line yet.
  const routeGeoJson = useMemo(() => {
    const pts = [];
    if (d?.pickup?.lat)         pts.push([d.pickup.lng,         d.pickup.lat]);
    if (d?.agent_current?.lat)  pts.push([d.agent_current.lng,  d.agent_current.lat]);
    if (d?.dropoff?.lat)        pts.push([d.dropoff.lng,        d.dropoff.lat]);
    if (pts.length < 2) return null;
    return {
      type: "Feature",
      properties: { name: "ride-route" },
      geometry:   { type: "LineString", coordinates: pts },
    };
  }, [d]);

  const center = d?.agent_current?.lat
    ? [d.agent_current.lat, d.agent_current.lng]
    : d?.pickup?.lat
      ? [d.pickup.lat, d.pickup.lng]
      : [19.076, 72.8777];

  const submitPanic = async () => {
    try {
      const r = await api.post(`/delivery/${delivery_id}/panic`, { note: "SOS" });
      setShareUrl(r.data?.share_url || null);
      setPanicSms(r.data?.sms || null);
      setPanicOpen(true);
    } catch {
      setPanicOpen(true);
      setShareUrl(null);
      setPanicSms(null);
    }
  };

  if (!d && offline) {
    return (
      <div className="p-8 text-center" data-testid="ride-error">
        <WifiOff size={28} className="mx-auto text-stone-300" />
        <div className="mt-2 font-bold">Oops, Connection Lost</div>
        <div className="text-xs text-stone-500 mt-1">Please check your internet connection.</div>
      </div>
    );
  }

  const statusLabel = {
    requested: "Finding a driver…",
    assigned:  "Driver is on the way",
    picked_up: "Picked up",
    in_transit: "Driver is nearby",
    delivered: "Trip complete",
    cancelled: "Cancelled",
  }[d?.status] || "…";

  return (
    <div className="fixed inset-0 bg-white z-[8000] flex flex-col" data-testid="ride-track">
      {/* Offline banner */}
      {offline && (
        <div className="bg-rose-100 text-rose-800 px-4 py-3 text-sm font-bold flex items-center gap-2" data-testid="ride-offline">
          <WifiOff size={16} /> Oops, Connection Lost — Please check your internet connection.
        </div>
      )}

      {/* Header bar */}
      <div className="px-4 py-3 flex items-center gap-2 bg-white border-b border-stone-200">
        <Link to="/services" className="p-2 -ml-2 rounded-full hover:bg-stone-100" data-testid="ride-close">
          <ChevronDown size={18} />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Live trip</div>
          <div className="font-bold text-stone-900 truncate">{statusLabel}</div>
        </div>
        <button onClick={submitPanic} className="text-xs font-bold rounded-full bg-white border border-emerald-200 text-emerald-800 px-3 py-1.5 inline-flex items-center gap-1" data-testid="ride-safety">
          <Shield size={12} /> Safety Centre
        </button>
      </div>

      {/* Map · Slice O · MapLibre GL with Grab-styled route line + explicit pins */}
      <div className="relative flex-1 min-h-[300px]" data-testid="ride-map">
        <PlacesMap
          center={center}
          zoom={16}
          pois={false}
          height="100%"
          routeGeoJson={routeGeoJson}
          markers={markers.map(m => ({
            key:   m.key,
            lat:   m.lat,
            lng:   m.lng,
            label: m.label,
            pulse: m.pulse,
            color: m.key === "drv" ? "#1FBF7F"
                 : m.key === "pu"  ? "#0f766e"
                 : m.key === "do"  ? "#e11d48"
                 : "#334155",
          }))}
          followKey={d?.agent_current?.lat ? "drv" : null}
        />
        {/* Slice P · driver's latest message floats over the map */}
        {d?.agent_id && (
          <DriverChatBubble agentId={d.agent_id} agentName={d.agent_name || "Driver"} />
        )}
        {d?.status === "in_transit" && (
          <div className="absolute top-3 right-3 bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full shadow">
            Live
          </div>
        )}
      </div>

      {/* Bottom sheet */}
      <div className={`bg-white border-t border-stone-200 shadow-2xl transition-all ${sheetOpen ? "" : "pb-2"}`} data-testid="ride-sheet">
        <button onClick={() => setSheetOpen(o => !o)} className="w-full pt-2 pb-1 grid place-items-center">
          <div className="w-10 h-1.5 bg-stone-300 rounded-full" />
        </button>
        {sheetOpen && (
          <div className="p-4">
            <div className="heading font-bold text-lg text-stone-900">{statusLabel}</div>
            <div className="text-xs text-stone-500">{d?.dropoff?.address || d?.pickup?.address || "Trip"}</div>

            <div className="mt-3 flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-stone-100 grid place-items-center overflow-hidden">
                {d?.agent_photo
                  ? <img src={d.agent_photo} alt="" className="w-full h-full object-cover" />
                  : <span className="text-stone-400">?</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-stone-900 truncate">
                  {d?.agent_name || "No driver yet"}
                  {d?.agent_rating && (
                    <span className="text-xs text-stone-500 ml-2 inline-flex items-center gap-0.5">
                      · <Star size={11} className="fill-amber-400 text-amber-400" /> {d.agent_rating}
                    </span>
                  )}
                </div>
                <div className="text-xs text-stone-500 truncate">
                  {d?.vehicle_plate || d?.agent_current?.plate || "Vehicle assigned on pickup"}
                </div>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <Link
                to={`/messages/${d?.agent_id || ""}`}
                className={`text-sm font-bold rounded-full px-3 py-2 inline-flex items-center justify-center gap-1.5 border ${d?.agent_id ? "border-stone-300 text-stone-800 bg-white hover:bg-stone-50" : "border-stone-200 text-stone-400 bg-stone-100 pointer-events-none"}`}
                data-testid="ride-chat"
              >
                <MessageCircle size={14} /> Chat with your driver
              </Link>
              <a
                href={d?.agent_phone ? `tel:${d.agent_phone}` : "#"}
                className={`text-sm font-bold rounded-full px-3 py-2 inline-flex items-center justify-center gap-1.5 border ${d?.agent_phone ? "border-emerald-600 text-emerald-700 hover:bg-emerald-50" : "border-stone-200 text-stone-400 bg-stone-100 pointer-events-none"}`}
                data-testid="ride-call"
              >
                <Phone size={14} /> Call
              </a>
            </div>

            {/* In-app VoIP call: customer ↔ assigned agent, authorised server-side by the live assignment */}
            {d?.agent_id && (user?.user_id === d.agent_id || [d.sender_id, d.recipient_id].includes(user?.user_id)) && (
              <RideCall rideId={delivery_id} active={RIDE_CALL_ACTIVE.includes(d.status)}
                        peerName={user?.user_id === d.agent_id ? (d.sender_name || "Customer") : (d.agent_name || "Driver")} />
            )}

            {/* Post-trip rating */}
            {d?.status === "delivered" && d?.agent_id && (
              <>
                <RateDriver deliveryId={delivery_id} />
                {/* META-2 · verified reviews scoped to this trip */}
                <ReviewSection
                  entityType="cab_trip"
                  entityId={delivery_id}
                  entityLabel="trip"
                />
              </>
            )}

            {/* Split fare */}
            {d?.share_token && d?.status !== "cancelled" && d?.status !== "delivered" && (
              <SplitFareRow deliveryId={delivery_id} shareToken={d.share_token} coPayers={d.co_payers || []} />
            )}

            {/* Quick-message templates */}
            {d?.agent_id && d?.status !== "cancelled" && (
              <ChatTemplates deliveryId={delivery_id} agentId={d.agent_id} />
            )}

            {/* Panic strip */}
            <button
              onClick={submitPanic}
              className="mt-3 w-full text-xs font-bold rounded-full bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 inline-flex items-center justify-center gap-1"
              data-testid="ride-panic"
            >
              <AlertTriangle size={12} /> Panic — share live trip with contacts
            </button>
          </div>
        )}
      </div>

      {panicOpen && (
        <PanicModal onClose={() => setPanicOpen(false)} shareUrl={shareUrl} sms={panicSms} />
      )}


    </div>
  );
}


function PanicModal({ onClose, shareUrl, sms }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = shareUrl ? `${typeof window !== "undefined" ? window.location.origin : ""}${shareUrl}` : null;
  const smsSent  = sms?.sent || 0;
  const smsTotal = sms?.contacts || 0;
  return (
    <div className="fixed inset-0 bg-black/60 z-[9800] grid place-items-center p-6" onClick={onClose} data-testid="ride-panic-modal">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <div className="heading font-bold text-lg text-stone-900 flex items-center gap-2">
            <AlertTriangle size={16} className="text-rose-600" /> Panic sent
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-stone-100" data-testid="ride-panic-close">
            <X size={16} />
          </button>
        </div>
        <div className="text-sm text-stone-600">
          Emergency contacts and Find2Near safety operators have been notified.
          {fullUrl && " Share the live trip link so anyone can watch you until you arrive."}
        </div>
        {smsTotal > 0 ? (
          <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-800" data-testid="ride-panic-sms">
            SMS sent to {smsSent} of {smsTotal} emergency contact{smsTotal === 1 ? "" : "s"}
          </div>
        ) : (
          <div className="mt-3 rounded-xl bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-800" data-testid="ride-panic-sms-empty">
            No emergency contacts on file — <Link to="/settings/emergency" className="underline font-bold">add up to 3</Link> so we can SMS them next time.
          </div>
        )}
        {fullUrl && (
          <div className="mt-3 bg-stone-50 rounded-xl p-3 text-[11px] font-mono break-all text-stone-700" data-testid="ride-panic-url">
            {fullUrl}
          </div>
        )}
        <div className="mt-3 flex gap-2">
          {fullUrl && (
            <button
              onClick={() => { navigator.clipboard.writeText(fullUrl); setCopied(true); }}
              className="flex-1 rounded-full bg-stone-900 text-white text-sm font-bold px-3 py-2 inline-flex items-center justify-center gap-1"
              data-testid="ride-panic-copy"
            >
              <Copy size={12} /> {copied ? "Copied" : "Copy link"}
            </button>
          )}
          <Link
            to="/emergency"
            className="flex-1 rounded-full border border-rose-300 text-rose-700 text-sm font-bold px-3 py-2 inline-flex items-center justify-center gap-1"
            data-testid="ride-panic-emergency"
          >
            <Shield size={12} /> Safety Centre
          </Link>
        </div>
      </div>
    </div>
  );
}


function RateDriver({ deliveryId }) {
  const [stars, setStars] = useState(0);
  const [sent, setSent]   = useState(false);
  const [busy, setBusy]   = useState(false);
  const submit = async (n) => {
    setBusy(true); setStars(n);
    try {
      await api.post(`/delivery/${deliveryId}/rate`, { stars: n });
      setSent(true);
    } catch { setSent(false); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="ride-rate">
      <div className="text-xs font-bold text-amber-800 mb-2">
        {sent ? "Thanks for rating your driver!" : "How was your driver?"}
      </div>
      {!sent && (
        <div className="flex gap-1" data-testid="ride-rate-stars">
          {[1,2,3,4,5].map(n => (
            <button
              key={n}
              onClick={() => submit(n)}
              disabled={busy}
              className="p-1"
              data-testid={`ride-rate-star-${n}`}
              aria-label={`${n} stars`}
            >
              <Star size={22} className={n <= stars ? "fill-amber-500 text-amber-500" : "text-amber-300"} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}



function SplitFareRow({ deliveryId, shareToken, coPayers }) {
  const [copied, setCopied] = useState(false);
  const url = `${typeof window !== "undefined" ? window.location.origin : ""}/split/${deliveryId}?token=${shareToken}`;
  const payers = 1 + (coPayers?.length || 0);
  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: "Split this ride", text: `Share the fare with me on Find2Near`, url });
      else { await navigator.clipboard.writeText(url); setCopied(true); }
    } catch { /* user cancelled */ }
  };
  return (
    <div className="mt-3 p-3 rounded-xl border border-sky-200 bg-sky-50" data-testid="ride-split">
      <div className="flex items-center gap-2">
        <Users size={14} className="text-sky-700" />
        <div className="text-xs font-bold text-sky-800">
          Split fare ({payers} {payers === 1 ? "payer" : "payers"})
        </div>
      </div>
      <div className="text-[11px] text-sky-700 mt-1">
        Share this link with a friend — they open it and share the cost equally.
      </div>
      <button
        onClick={share}
        className="mt-2 w-full text-xs font-bold rounded-full bg-sky-600 hover:bg-sky-700 text-white px-3 py-2 inline-flex items-center justify-center gap-1"
        data-testid="ride-split-share"
      >
        <Copy size={12} /> {copied ? "Copied!" : "Share split link"}
      </button>
    </div>
  );
}


const CHAT_TEMPLATES = [
  "I'm at the pickup point 👋",
  "Please wait 2 min",
  "I'm at gate 4",
  "Please call when you arrive",
  "Thanks, on my way!",
  "Please cancel — sorry",
];

function ChatTemplates({ deliveryId, agentId }) {
  const [thread, setThread] = useState(null);
  const [busy, setBusy]     = useState(false);
  const [sent, setSent]     = useState(null);
  useEffect(() => {
    // Find (or create) a DM thread with the driver via existing NVN helper.
    api.post("/messenger/thread/start-by-user", { user_id: agentId })
      .then(r => setThread(r.data?.conv_id || r.data?.id))
      .catch(async () => {
        // Fall back: some deployments only expose start-by-nvn.
        try {
          const r2 = await api.post("/messenger/thread/start-by-nvn", { peer_user_id: agentId });
          setThread(r2.data?.conv_id || r2.data?.id);
        } catch { /* thread creation may need driver's NVN — silent */ }
      });
  }, [agentId]);
  const send = async (text) => {
    if (!thread) return;
    setBusy(true); setSent(null);
    try {
      await api.post(`/messenger/thread/${thread}/send`, { body: text, delivery_id: deliveryId });
      setSent(text);
    } catch { /* toast */ }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3" data-testid="ride-chat-templates">
      <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Quick message</div>
      <div className="flex gap-1.5 flex-wrap">
        {CHAT_TEMPLATES.map(t => (
          <button
            key={t}
            onClick={() => send(t)}
            disabled={busy || !thread}
            className="text-[11px] font-bold rounded-full border bg-white border-stone-200 hover:border-stone-300 text-stone-700 px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid={`chat-tpl-${t.slice(0, 10)}`}
          >
            <Send size={10} /> {t}
          </button>
        ))}
      </div>
      {sent && (
        <div className="mt-1 text-[10px] text-emerald-700">Sent: "{sent}"</div>
      )}
      {!thread && (
        <div className="mt-1 text-[10px] text-stone-400">Opening chat…</div>
      )}
    </div>
  );
}


// ─── Slice P · Floating chat callout on the map ─────────────────────────────
// Slice Q · Rider can inline-reply with quick-tap chips without opening chat.
const QUICK_REPLIES = ["OK", "Thanks", "Please cancel"];

function DriverChatBubble({ agentId, agentName }) {
  const [convId,  setConvId]  = useState(null);
  const [otherLang, setOtherLang] = useState(null);
  const [lastMsg, setLastMsg] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [replySent, setReplySent] = useState(null);
  const [replying,  setReplying]  = useState(false);
  const timerRef = useRef(null);
  const seenIdRef = useRef(null);

  // Resolve the DM thread with the driver.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.post("/messenger/thread/start-by-user", { user_id: agentId });
        if (!cancel) setConvId(r.data?.conv_id || r.data?.id || null);
      } catch {
        try {
          const r2 = await api.post("/messenger/thread/start-by-nvn", { peer_user_id: agentId });
          if (!cancel) setConvId(r2.data?.conv_id || r2.data?.id || null);
        } catch { /* silent */ }
      }
    })();
    return () => { cancel = true; };
  }, [agentId]);

  // Poll the last message every 4 s and pop the bubble when the driver
  // sends something new. Uses the same GET /conversations/{id} that
  // ChatThread uses so we inherit `participant_langs`.
  useEffect(() => {
    if (!convId) return;
    const tick = async () => {
      try {
        const [convR, msgR] = await Promise.all([
          api.get(`/conversations/${convId}`),
          api.get(`/conversations/${convId}/messages`),
        ]);
        const lang = convR.data?.participant_langs?.[agentId];
        if (lang) setOtherLang(lang);
        const rows = msgR.data?.messages || msgR.data || [];
        const fromDriver = [...rows].reverse().find(m => m.sender_id === agentId);
        if (fromDriver && fromDriver.msg_id !== seenIdRef.current) {
          seenIdRef.current = fromDriver.msg_id;
          setLastMsg(fromDriver);
          setDismissed(false);
          // Auto-dismiss after 10 s unless the user has closed it themselves.
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => setDismissed(true), 10000);
        }
      } catch { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 4000);
    return () => { clearInterval(iv); clearTimeout(timerRef.current); };
  }, [convId, agentId]);

  if (!lastMsg || dismissed) return null;
  const preview = lastMsg.body || (
    lastMsg.attachment_kind === "audio" ? "🎤 Voice note" :
    lastMsg.attachment_kind === "image" ? "📷 Photo" :
    lastMsg.attachment_kind === "video" ? "🎥 Video" : "📎 Attachment"
  );

  const sendReply = async (text) => {
    if (!convId || replying) return;
    setReplying(true);
    // Cancel the auto-dismiss timer while the rider is engaging with the bubble.
    clearTimeout(timerRef.current);
    try {
      await api.post(`/conversations/${convId}/messages`, { body: text });
      setReplySent(text);
      // Fade the bubble out ~1.6 s later so the rider sees confirmation.
      timerRef.current = setTimeout(() => setDismissed(true), 1600);
    } catch {
      /* toast handled elsewhere; keep bubble open so the rider can retry */
    } finally {
      setReplying(false);
    }
  };

  return (
    <div
      className="absolute top-3 left-3 right-3 md:right-auto md:max-w-sm z-10 pointer-events-auto"
      data-testid="ride-driver-bubble"
    >
      <div className="rounded-2xl bg-white shadow-lg border border-stone-100 p-3 flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-emerald-100 grid place-items-center shrink-0 text-emerald-700 text-xs font-bold">
          {(agentName || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase text-stone-400 tracking-wide">{agentName}</div>
          <div className="text-sm text-stone-900 whitespace-pre-wrap break-words">{preview}</div>
          {/* Auto-translate into the rider's UI language when the driver
              wrote in a different one — the "no linguistic chauvinism" chip. */}
          {lastMsg.body && <TranslateChip text={lastMsg.body} />}
          {lastMsg.body && (
            <div className="mt-1">
              <SpeakChip
                text={lastMsg.body}
                onlyWhenTranslated
                testId="ride-driver-bubble-text-speak"
              />
            </div>
          )}
          {/* Whisper transcript on voice notes with translate chip + speak chip. */}
          {lastMsg.attachment_kind === "audio" && lastMsg.attachment_transcript && (
            <>
              <div className="text-xs text-stone-600 italic mt-1">"{lastMsg.attachment_transcript}"</div>
              <TranslateChip text={lastMsg.attachment_transcript} />
              <div className="mt-1">
                <SpeakChip
                  text={lastMsg.attachment_transcript}
                  sourceLang={lastMsg.attachment_transcript_lang}
                  testId="ride-driver-bubble-speak"
                />
              </div>
            </>
          )}
          {/* Slice Q · quick-reply chips — inline reply without opening the sheet. */}
          {convId && (
            <div className="mt-2 flex gap-1.5 flex-wrap" data-testid="ride-driver-bubble-replies">
              {replySent ? (
                <div className="text-[10px] font-bold text-emerald-700" data-testid="ride-driver-bubble-sent">
                  ✓ Sent: "{replySent}"
                </div>
              ) : (
                QUICK_REPLIES.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => sendReply(t)}
                    disabled={replying}
                    className="text-[11px] font-bold rounded-full border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 px-2.5 py-1 disabled:opacity-50"
                    data-testid={`ride-driver-bubble-reply-${t.slice(0, 6).toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {t}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded-full hover:bg-stone-100 text-stone-400"
          aria-label="Dismiss"
          data-testid="ride-driver-bubble-close"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

